import React, { FC } from "react";
import get from "lodash/get";
import { getTotalSelectedOptionsPrice } from "@deeple-ai/shopping-cart-calculator";

import Grid from "components/Grid";
import {
  formatToReadablePrice,
  getPromotionDisplayNameForFlexMessage,
  getPromotionTotalDiscountForProductSKU,
  sortPromotionByTypeAndUpdatedAt,
} from "components/Message/utils/promotionUtil";
import Typography from "components/Typography";
import COLORS from "constants/Colors";
import { NO_IMG_AVAILABLE } from "constants/Image";
import { ProductType, ProductTypeType } from "types/Order";
import { PromotionType } from "types/Promotion";
import getProductOptions from "utils/order/getProductOptions";
import { AmountWrapper, ProductSKUImageSmall } from "../FlexMessage.style";

type FlexOrderProductSKUWithProductsPropsType = {
  groupedPromotionBySKUId: unknown;
  products: ProductType[];
  productPromotions: PromotionType[];
};

const FlexOrderProductSKUsWithProducts: FC<FlexOrderProductSKUWithProductsPropsType> = (props) => {
  const { groupedPromotionBySKUId, products, productPromotions } = props;

  return (
    <>
      {products.map((product: ProductType) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const totalSelectedOptionsPrice = getTotalSelectedOptionsPrice(product.options as any);

        const rawProductPrice = (Number(product.price) + Number(totalSelectedOptionsPrice)) * Number(product.amount);

        const productPrice = product.isFree ? "ฟรี" : `฿${formatToReadablePrice(rawProductPrice)}`;

        const productSKUPromotions = get(groupedPromotionBySKUId, `${product.id}`, []).sort(
          sortPromotionByTypeAndUpdatedAt,
        );

        const productSKUPromotion = productSKUPromotions[0];

        const promotionName = getPromotionDisplayNameForFlexMessage(productSKUPromotion);

        const totalDiscount = getPromotionTotalDiscountForProductSKU(product, productPromotions);

        let priceAfterDiscount = Number(rawProductPrice) - Number(totalDiscount);

        if (priceAfterDiscount < 0) {
          // to make sure product price will be more than or equal 0
          priceAfterDiscount = 0;
        }

        const isShowNewPriceStyle = !!totalDiscount;
        /**
         * if show new price that mean show new price per unit
         * but if no and there is totalDiscount from promotion then show discount
         */
        const priceToShow = isShowNewPriceStyle ? priceAfterDiscount : totalDiscount;

        return (
          <Grid
            key={product.id}
            container
            justify="flex-start"
            alignItems="flex-start"
            alignContent="flex-start"
            className="d-flex pb-2"
          >
            <Grid item>
              <ProductSKUImageSmall
                src={product.image || get(product, "product.images[0].src") || NO_IMG_AVAILABLE}
                className="pr-1"
              />
            </Grid>
            <Grid item className="" style={{ flex: 1 }}>
              <Grid
                container
                direction="column"
                alignItems="flex-start"
                alignContent="flex-start"
                // className="wordBreakAll"
              >
                <Typography variant="title10" color={COLORS.Dark} className=" text-left py-1 wordBreakAll">
                  {product.product.name}
                </Typography>

                <Grid item container justify="space-between" alignItems="center" alignContent="flex-start">
                  <Grid item container justify="space-between" alignItems="center" alignContent="flex-start">
                    <Grid item xs={2}>
                      <AmountWrapper className="text-center" style={{ fontSize: "10px" }}>
                        {`${product.amount}x`}
                      </AmountWrapper>
                    </Grid>

                    <Grid item xs={5}>
                      <Grid item className="pl-2">
                        <Typography
                          variant="body4"
                          color={COLORS.Dark}
                          className="text-left py-1 hiddenOverFlowText"
                          style={{ flex: 1 }}
                        >
                          {product.productType.length
                            ? `${product.productType
                                .map((productType: ProductTypeType) => productType.value)
                                .join(", ")}`
                            : ""}
                        </Typography>

                        {product.options && product.options.length > 0 && (
                          <Typography variant="body4" color={COLORS.Dark} className="text-left py-1 hiddenOverFlowText">
                            {getProductOptions(product.options || [])}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>

                    <Grid item xs={5}>
                      <Typography variant="title10" color={COLORS.Primary} className="text-right py-1">
                        {priceToShow ? (
                          <>
                            <div style={{ color: COLORS.DarkLight, textDecoration: "line-through" }}>
                              {productPrice}
                            </div>
                            <div>{`฿${formatToReadablePrice(priceToShow)}`}</div>
                          </>
                        ) : (
                          <div>{productPrice}</div>
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              {product.note && (
                <Typography variant="body5" color={COLORS.Dark} className="text-left py-1 hiddenOverFlowText">
                  {product.note}
                </Typography>
              )}
              {promotionName && (
                <Typography variant="body5" color={COLORS.Error} className="text-left pt-1">
                  {promotionName}
                </Typography>
              )}
            </Grid>
          </Grid>
        );
      })}
    </>
  );
};

export default FlexOrderProductSKUsWithProducts;
